import dayjs from 'dayjs';
import { setIn } from 'timm';
import { call, delay, put } from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import claimApiClient from '../../../api/claims/client';
import type {
  Claim,
  Insight,
  InsightTemplate,
  Summary,
  SummaryTemplate,
} from '../../../api/claims/interface';
import templatesApiClient, {
  CREATE_TEMPLATE_TIMEOUT,
} from '../../../api/templates/client';
import type {
  ClaimTemplate,
  ClaimTemplateListItem,
  TemplateSchema,
  TemplateStateResponse,
} from '../../../api/templates/interface';

export interface ClaimOverviewState {
  claimMetadata: {
    [dossier_id: string]: Claim;
  };
  claimTemplates: {
    [dossier_id: string]: {
      templates: ClaimTemplateListItem[];
      has_available_template: Record<string, boolean>;
      template_schemas: Record<string, TemplateSchema>;
    };
  };
  claimTemplateDetails: {
    [dossier_id: string]: {
      [template_id: string]: ClaimTemplate;
    };
  };
  insights: {
    [dossier_id: string]: Insight[];
  };
  summaries: {
    [dossier_id: string]: Summary[];
  };
  summaryTemplates: {
    [dossier_id: string]: SummaryTemplate[];
  };
  insightsTemplates: {
    [dossier_id: string]: InsightTemplate[];
  };
  notepad: {
    [dossier_id: string]: string;
  };
  isDrawerOpen: boolean;
  actions: {
    fetchClaim: AsyncActionState<Claim>;
    fetchClaimTemplates: AsyncActionState<{
      templates: ClaimTemplateListItem[];
      has_available_template: boolean;
    }>;
    fetchClaimTemplateData: AsyncActionState<ClaimTemplate[]>;
    fetchClaimTemplateState: AsyncActionState<TemplateStateResponse>;
    fetchInsights: AsyncActionState<Insight[]>;
    fetchInsightsTemplates: AsyncActionState<InsightTemplate[]>;
    fetchSummaries: AsyncActionState<Summary[]>;
    fetchSummaryTemplates: AsyncActionState<SummaryTemplate[]>;
    fetchNotepad: AsyncActionState<Summary[]>;
    downloadRtfFile: AsyncActionState<void>;
    downloadHtmlTemplateAsRtf: AsyncActionState<void>;
    copyTemplateRtf: AsyncActionState<void>;
    createClaimTemplate: AsyncActionState<{ __noContent?: boolean }>;
  };
}

const initialState: ClaimOverviewState = {
  claimMetadata: {},
  claimTemplates: {},
  claimTemplateDetails: {},
  insights: {},
  summaries: {},
  summaryTemplates: {},
  insightsTemplates: {},
  notepad: {},
  isDrawerOpen: false,
  actions: {
    fetchClaim: { status: 'uninitialized' },
    fetchClaimTemplates: { status: 'uninitialized' },
    fetchClaimTemplateData: { status: 'uninitialized' },
    fetchClaimTemplateState: { status: 'uninitialized' },
    fetchInsights: { status: 'uninitialized' },
    fetchInsightsTemplates: { status: 'uninitialized' },
    fetchSummaries: { status: 'uninitialized' },
    fetchSummaryTemplates: { status: 'uninitialized' },
    fetchNotepad: { status: 'uninitialized' },
    downloadRtfFile: { status: 'uninitialized' },
    downloadHtmlTemplateAsRtf: { status: 'uninitialized' },
    copyTemplateRtf: { status: 'uninitialized' },
    createClaimTemplate: { status: 'uninitialized' },
  },
};

function* downloadRtfFile(action: {
  payload: {
    claimId: string;
    rtfContent: string;
    templateType: string;
  };
}) {
  // force async
  yield* delay(0);
  const { claimId, rtfContent, templateType } = action.payload;
  const blob = new Blob([rtfContent], { type: 'application/rtf' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${claimId}-${templateType}`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function* downloadHtmlTemplateAsRtf(action: {
  payload: {
    dossierId: string;
    templateId: string;
    templateType: string;
    clientDossierId: string;
    template_version_id?: string;
  };
}) {
  const {
    dossierId,
    templateId,
    templateType,
    clientDossierId,
    template_version_id,
  } = action.payload;
  const response = yield* call(templatesApiClient.downloadHtmlTemplateAsRtf, {
    dossierId,
    templateId,
    template_version_id,
  });

  const blob = new Blob([response.rtf_content || ''], {
    type: 'application/rtf',
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${clientDossierId}-${templateType}.rtf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function* copyTemplateRtf(action: { payload: { rtfContent: string } }) {
  yield* delay(0);

  const { rtfContent } = action.payload;
  yield call([navigator.clipboard, navigator.clipboard.writeText], rtfContent);
}

function* fetchClaimTemplateData(action: {
  payload: {
    dossier_id: string;
    template_id: string;
  };
}) {
  const { dossier_id, template_id } = action.payload;

  const response = yield* call(templatesApiClient.fetchDetail, action.payload);

  if (typeof response === 'string') {
    yield put({
      type: 'chat/setTemplateVersions',
      payload: {
        dossierId: dossier_id,
        templateId: template_id,
        templateVersions: [
          {
            html: response,
          },
        ],
        overwrite: true,
      },
    });
  }

  return response;
}

function* fetchClaimTemplateState(action: {
  payload: {
    dossier_id: string;
    template_id: string;
  };
}) {
  const { dossier_id, template_id } = action.payload;

  const response = yield* call(
    templatesApiClient.fetchTemplateState,
    action.payload
  );

  const sortedVersions = [...response.versions].sort(
    (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf()
  );

  yield put({
    type: 'chat/setTemplateVersions',
    payload: {
      dossierId: dossier_id,
      templateId: template_id,
      templateVersions: sortedVersions,
      overwrite: true,
    },
  });
  yield put({
    type: 'chat/setChatMessages',
    payload: {
      dossierId: dossier_id,
      messages: response.thread?.messages ?? [],
    },
  });
  yield put({
    type: 'chat/setChatThread',
    payload: {
      dossierId: dossier_id,
      templateId: template_id,
      threadId: response.thread_id ?? uuid(),
    },
  });

  return response;
}

function* restoreTemplateVersion(action: {
  payload: {
    dossier_id: string;
    template_id: string;
    version_id: string;
  };
}) {
  const { dossier_id, template_id } = action.payload;
  const response = yield* call(
    templatesApiClient.restoreTemplateVersion,
    action.payload
  );

  yield put({
    type: 'chat/setTemplateVersions',
    payload: {
      dossierId: dossier_id,
      templateId: template_id,
      templateVersions: [response.template_version],
    },
  });

  return response;
}

const slice = setupSlice('claim-overview', initialState)
  .addAsyncSagas({
    fetchClaim: invoke(claimApiClient.fetchClaim),
    fetchClaimTemplates: invoke(templatesApiClient.fetch),
    fetchClaimTemplateData,
    fetchClaimTemplateState,
    fetchInsights: invoke(claimApiClient.fetchInsights),
    fetchInsightsTemplates: invoke(claimApiClient.fetchInsightsTemplates),
    fetchSummaries: invoke(claimApiClient.fetchSummaries),
    fetchSummaryTemplates: invoke(claimApiClient.fetchSummaryTemplates),
    fetchNotepad: invoke(claimApiClient.fetchNotepad),
    downloadRtfFile,
    downloadHtmlTemplateAsRtf,
    copyTemplateRtf,
    beginProcessing: invoke(claimApiClient.beginProcessing),
    restoreTemplateVersion,
  })
  .addAsyncSagas(
    {
      createClaimTemplate: invoke(templatesApiClient.create),
    },
    {
      timeoutLimit: CREATE_TEMPLATE_TIMEOUT,
    }
  )
  .addReducers({
    'fetchClaim/fulfilled': (state, action) => {
      state.claimMetadata[action.meta.arg.dossierId] = action.payload;
    },
    'fetchClaimTemplates/fulfilled': (state, action) => {
      state.claimTemplates = setIn(
        state.claimTemplates,
        [action.meta.arg.dossier_id],
        action.payload
      ) as typeof state.claimTemplates;
    },
    'fetchClaimTemplateData/fulfilled': (state, action) => {
      state.claimTemplateDetails = setIn(
        state.claimTemplateDetails,
        [action.meta.arg.dossier_id, action.meta.arg.template_id],
        action.payload
      ) as typeof state.claimTemplateDetails;
    },
    'fetchInsights/fulfilled': (state, action) => {
      state.insights[action.meta.arg.dossierId] = action.payload;
    },
    'fetchInsightsTemplates/fulfilled': (state, action) => {
      state.insightsTemplates[action.meta.arg.dossierId] = action.payload;
    },
    'fetchSummaries/fulfilled': (state, action) => {
      state.summaries[action.meta.arg.dossierId] = action.payload;
    },
    'fetchSummaryTemplates/fulfilled': (state, action) => {
      state.summaryTemplates[action.meta.arg.dossierId] = action.payload;
    },
    'fetchNotepad/fulfilled': (state, action) => {
      state.notepad[action.meta.arg.dossierId] = action.payload;
    },
  })
  .addReducers({
    setDrawerOpen: (state, action: { payload: boolean }) => {
      state.isDrawerOpen = action.payload;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
